import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTwilioVoice } from '../context/TwilioVoiceContext';
import { useTwilioNotifications } from '../context/TwilioNotificationContext';
import ActiveExternalCall from './ActiveExternalCall';
import EmailSignaturePreview from './EmailSignaturePreview';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { X, Send, Save, Loader2, Phone, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ContactBottomSheet — global bottom-sheet that opens when the user
 * clicks a phone or email icon inside an override-request popup (or
 * any future place that fires `open-contact-sheet`).
 *
 * Trigger via window event:
 *   window.dispatchEvent(new CustomEvent('open-contact-sheet', {
 *     detail: { mode: 'call' | 'email', value: '<phone-or-email>',
 *               contactName?: string, subject?: string, body?: string }
 *   }))
 *
 * Behaviour (spec):
 *   • Slides up from bottom to 5/6 viewport height.
 *   • Drag the handle to lower to 2/6 (still visible).
 *   • Two stop points: 2/6 and 5/6 (snap on release).
 *   • Persists until X is clicked OR (call mode) the call ends.
 *   • Call mode: immediately initiates outbound call via
 *     TwilioVoiceContext.placeCall(...) — full call UI from the
 *     existing ActiveExternalCall component renders inside the sheet.
 *     X button hangs up + closes.
 *   • Email mode: in-sheet compose form (To, Subject, Body). Send
 *     button posts /email/send. X saves a draft via POST /email/drafts
 *     then closes.
 */
export default function ContactBottomSheet() {
  const { api, user } = useAuth();
  const twilioVoice = useTwilioVoice();
  const { activeExternalCall, setOutboundCall, patchActiveCall, clearActiveCall } = useTwilioNotifications();

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('call'); // 'call' | 'email'
  const [target, setTarget] = useState({ value: '', contactName: null });

  // Height stops — fraction of viewport.
  const STOPS = { low: 2 / 6, high: 5 / 6 };
  const [heightFrac, setHeightFrac] = useState(5 / 6);
  const dragStartY = useRef(null);
  const dragStartFrac = useRef(null);

  // Email compose state
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeState, setComposeState] = useState('');
  const [stateInboxes, setStateInboxes] = useState([]);
  const [sending, setSending] = useState(false);
  const [draftId, setDraftId] = useState(null);

  // ── open/close lifecycle ───────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      const { mode: m, value, contactName, subject, body } = e.detail || {};
      if (!value || !m) return;
      setMode(m);
      setTarget({ value, contactName: contactName || null });
      setComposeSubject(subject || '');
      setComposeBody(body || '');
      setHeightFrac(STOPS.high);
      setOpen(true);
    };
    window.addEventListener('open-contact-sheet', handler);
    return () => window.removeEventListener('open-contact-sheet', handler);
  }, []);

  // Kick off the call AT MOUNT? No — placeCall requires a user-
  // gesture (mic permission prompt) and useEffect runs after render,
  // outside the gesture chain. We expose a "Start Call" button inside
  // the sheet so the user's click is the gesture that drives Twilio.
  const [callStatus, setCallStatus] = useState('idle'); // idle | connecting | live | failed
  const startCall = useCallback(async () => {
    setCallStatus('connecting');
    try {
      const call = await twilioVoice.placeCall(target.value, {
        contactName: target.contactName,
        state: user?.state || 'NC',
      });
      setOutboundCall({
        call_sid: call?.parameters?.CallSid || null,
        _sdkCall: call,
        to_number: target.value,
        contact_name: target.contactName,
        direction: 'outbound',
        started_at: new Date().toISOString(),
        state: user?.state || 'NC',
      });
      setCallStatus('live');
    } catch (e) {
      setCallStatus('failed');
    }
  }, [twilioVoice, target, user?.state, setOutboundCall]);

  // Reset call status when sheet opens fresh.
  useEffect(() => {
    if (open && mode === 'call') setCallStatus('idle');
  }, [open, mode]);

  // Load state inboxes for email mode (the user picks which mailbox to send from).
  useEffect(() => {
    if (!open || mode !== 'email' || stateInboxes.length > 0) return;
    api.get('/email/inboxes').then((r) => {
      const list = r.data?.inboxes || r.data || [];
      setStateInboxes(list);
      // Default to the user's own state if present, else first inbox.
      const own = list.find((x) => (x.state || '').toUpperCase() === (user?.state || '').toUpperCase());
      setComposeState((own || list[0])?.state || '');
    }).catch(() => {});
  }, [open, mode, api, stateInboxes.length, user?.state]);

  // ── drag-to-resize ──────────────────────────────────────────
  // Listeners attach once when the sheet first opens; they check
  // the `dragStartY` ref each call. Doing it this way (instead of
  // re-attaching on every drag start) means we never miss the first
  // few pixels of a fast swipe.
  useEffect(() => {
    if (!open) return undefined;
    const onMove = (e) => {
      if (dragStartY.current == null) return;
      e.preventDefault();
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dy = clientY - dragStartY.current;
      const vh = window.innerHeight;
      const next = Math.max(STOPS.low, Math.min(STOPS.high, dragStartFrac.current - dy / vh));
      setHeightFrac(next);
    };
    const onUp = () => {
      if (dragStartY.current == null) return;
      const startedAt = dragStartFrac.current;
      dragStartY.current = null;
      dragStartFrac.current = null;
      // Snap policy: if user dragged down ≥10% of viewport from their
      // start position, snap to the LOW stop (2/6). Otherwise snap
      // back to wherever they started. This makes a small downward
      // gesture actually do something.
      setHeightFrac((cur) => {
        const downDelta = startedAt - cur;
        if (downDelta > 0.1) return STOPS.low;
        const upDelta = cur - startedAt;
        if (upDelta > 0.1) return STOPS.high;
        // No meaningful movement → snap to nearest standard stop.
        return cur < (STOPS.low + STOPS.high) / 2 ? STOPS.low : STOPS.high;
      });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
    document.addEventListener('touchcancel', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
      document.removeEventListener('touchcancel', onUp);
    };
  }, [open]);

  const onDragStart = (clientY, e) => {
    if (e?.preventDefault) e.preventDefault();
    dragStartY.current = clientY;
    dragStartFrac.current = heightFrac;
  };

  // ── close handlers ─────────────────────────────────────────────
  const handleCallEnd = useCallback(() => {
    clearActiveCall();
    setOpen(false);
  }, [clearActiveCall]);

  const closeAndCleanup = async () => {
    if (mode === 'call') {
      // Hang up the live call if still up.
      try {
        const sdk = activeExternalCall?._sdkCall || twilioVoice?.activeCall?.call;
        if (sdk) sdk.disconnect();
      } catch (e) { /* noop */ }
      clearActiveCall();
      setOpen(false);
      return;
    }
    // Email mode — save draft on close.
    if (composeSubject.trim() || composeBody.trim()) {
      try {
        if (!draftId && composeState) {
          const res = await api.post('/email/drafts', {
            state: composeState,
            to: target.value,
            cc: '',
            subject: composeSubject,
            body: composeBody,
          });
          if (res.data?.id) setDraftId(res.data.id);
          toast.success('Saved as draft');
        }
      } catch (e) { /* fail silently */ }
    }
    setOpen(false);
  };

  const sendEmail = async () => {
    if (!composeState) { toast.error('Pick a mailbox to send from'); return; }
    if (!composeSubject.trim()) { toast.error('Subject required'); return; }
    setSending(true);
    try {
      await api.post('/email/send', {
        state: composeState,
        to: [target.value],
        cc: [],
        subject: composeSubject,
        body: composeBody,
        draft_id: draftId,
      });
      toast.success('Email sent');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[200] bg-[#d4e4cf] dark:bg-[#141414] rounded-t-2xl shadow-2xl border-t flex flex-col pointer-events-auto"
      style={{ height: `${heightFrac * 100}vh`, transition: dragStartY.current == null ? 'height 0.18s ease-out' : 'none' }}
      data-testid="contact-bottom-sheet"
    >
      {/* Drag region — covers the handle AND the title bar so users can
          grab anywhere along the top of the sheet (desktop mouse or
          mobile touch) to resize. The action buttons inside the title
          row stop propagation so they don't trigger drag. */}
      <div
        className="cursor-row-resize select-none touch-none"
        onMouseDown={(e) => onDragStart(e.clientY, e)}
        onTouchStart={(e) => onDragStart(e.touches[0].clientY, e)}
        data-testid="contact-bottom-sheet-drag-region"
      >
        <div className="flex items-center justify-center pt-3 pb-2" data-testid="contact-bottom-sheet-handle">
          <div className="w-14 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500" />
        </div>
        <div className="px-3 pb-2 flex items-center justify-between border-b">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground truncate pr-2">
            {mode === 'call' ? 'Call' : 'Email'}: {target.contactName || target.value}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setHeightFrac((cur) => (cur <= (STOPS.low + STOPS.high) / 2 ? STOPS.high : STOPS.low));
              }}
              className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
              aria-label={heightFrac > (STOPS.low + STOPS.high) / 2 ? 'Collapse sheet' : 'Expand sheet'}
              data-testid="contact-bottom-sheet-collapse"
            >
              {heightFrac > (STOPS.low + STOPS.high) / 2
                ? <ChevronDown className="w-4 h-4" />
                : <ChevronUp className="w-4 h-4" />}
            </button>
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); closeAndCleanup(); }}
              className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
              aria-label="Close"
              data-testid="contact-bottom-sheet-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-3 min-h-0">
        {mode === 'call' ? (
          callStatus === 'live' && activeExternalCall ? (
            <ActiveExternalCall callData={activeExternalCall} onCallEnd={handleCallEnd} />
          ) : callStatus === 'connecting' ? (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground text-sm gap-2">
              <Loader2 className="w-6 h-6 animate-spin" />
              Connecting to {target.contactName || target.value}…
            </div>
          ) : callStatus === 'failed' ? (
            <div className="h-full flex flex-col items-center justify-center text-sm gap-3 text-center px-6">
              <p className="text-red-600 font-medium">Could not connect the call.</p>
              <p className="text-xs text-muted-foreground">
                Your phone device isn't ready yet, or the microphone is blocked. Open the Calls page once to register your device, or check microphone permissions in your browser.
              </p>
              <Button size="sm" className="bg-[#586D52] hover:bg-[#4a5d46]" onClick={startCall} data-testid="contact-sheet-retry-call">
                Try Again
              </Button>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-sm gap-3 text-center px-6">
              <p className="font-semibold text-base">Call {target.contactName || target.value}?</p>
              <p className="text-xs text-muted-foreground">{target.value}</p>
              <Button size="lg" className="bg-[#586D52] hover:bg-[#4a5d46] text-white rounded-full px-6" onClick={startCall} data-testid="contact-sheet-start-call">
                <Phone className="w-4 h-4 mr-2" /> Start Call
              </Button>
              <p className="text-[11px] text-muted-foreground max-w-[260px]">
                Your microphone will activate. End the call by tapping the hangup button or closing this sheet.
              </p>
            </div>
          )
        ) : (
          <div className="flex flex-col h-full gap-2">
            {stateInboxes.length > 1 && (
              <select
                value={composeState}
                onChange={(e) => setComposeState(e.target.value)}
                className="w-full h-9 px-2 text-xs rounded-md border bg-background flex-shrink-0"
                data-testid="contact-sheet-mailbox"
              >
                {stateInboxes.map((x) => (
                  <option key={x.id || x.state} value={x.state}>From: {x.state} — {x.email_address}</option>
                ))}
              </select>
            )}
            <div className="text-xs text-muted-foreground px-1 flex-shrink-0" data-testid="contact-sheet-to">
              To: <span className="font-medium text-foreground">{target.value}</span>
            </div>
            <Input
              value={composeSubject}
              onChange={(e) => setComposeSubject(e.target.value)}
              placeholder="Subject"
              className="h-9 text-sm flex-shrink-0"
              data-testid="contact-sheet-subject"
            />
            <Textarea
              value={composeBody}
              onChange={(e) => setComposeBody(e.target.value)}
              placeholder="Type your message…"
              className="flex-1 min-h-[80px] text-sm resize-none"
              data-testid="contact-sheet-body"
            />
            {/* Business-card signature footer — auto-appended server-side
                on send. Rendered here so the recipient view is obvious. */}
            <div className="flex-shrink-0">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 px-1">Your signature (auto-appended)</p>
              <EmailSignaturePreview user={user} onOpenPreview={() => {}} />
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button size="sm" variant="outline" className="flex-1 h-9" onClick={closeAndCleanup} disabled={sending} data-testid="contact-sheet-save-draft">
                <Save className="w-3.5 h-3.5 mr-1.5" />
                Save & Close
              </Button>
              <Button size="sm" className="flex-1 h-9 bg-[#586D52] hover:bg-[#4a5d46]" onClick={sendEmail} disabled={sending} data-testid="contact-sheet-send">
                {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
